import React, { useEffect, useRef, useCallback, useState } from "react";
import { Box, Button, InputBase, Paper, styled } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../../constants";
import SearchSuggestionsBlock from "../SearchSuggestionsBlock/SearchSuggestionsBlock";
import ResultsSuggestionsBlock from "../ResultsSuggestionsBlock/ResultsSuggestionsBlock";
import { getVendors } from "../../api/searchController";

const SearchBarContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    maxWidth: "25rem",
    width: "100%",
    display: "flex",
    alignItems: "center",
    border: `0.5px solid ${COLORS.border}`,
    borderRadius: "var(--border-radius-xs)",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    padding: "4px",
    marginLeft: "var(--spacing-s)",
    fontSize: "0.8rem",
}));

const SearchButton = styled(Button)(({ theme }) => ({
    backgroundColor: COLORS.themeYellow,
    color: COLORS.darkText,
    fontSize: "var(--font-size-xs)",
    fontWeight: 500,
    fontFamily: "Rubik",
    padding: "var(--spacing-xs) var(--spacing-m)",
    borderRadius: "0 var(--border-radius-s) var(--border-radius-xs) 0",
    textTransform: "uppercase",
}));

const CloseButton = styled(Button)(({ theme }) => ({
    backgroundColor: COLORS.themeRed,
    color: COLORS.white,
    fontSize: "var(--font-size-xs)",
    fontWeight: 500,
    fontFamily: "Rubik",
    padding: "var(--spacing-xs) var(--spacing-m)",
    borderRadius: "0 var(--border-radius-s) var(--border-radius-xs) 0",
    "&:hover": {
        backgroundColor: theme.palette.error.dark,
    },
}));

const SearchBar = ({
                       searchInput,
                       setSearchInput,
                       searchDropdownOpen,
                       setSearchDropdownOpen,
                       loading,
                       products,
                       setSearchTerm,
                       handleAddToCart,
                       setAccountDropdownOpen,
                       categoriesAndTypes,
                   }) => {
    const searchContainerRef = useRef(null);
    const searchInputRef = useRef(null);
    const [vendors, setVendors] = useState([]);

    useEffect(() => {
        getVendors().then((r) => { if (r?.success) setVendors(r.data ?? []); });
    }, []);

    const handleSearchFocus = useCallback(() => {
        setSearchDropdownOpen(true);
        setAccountDropdownOpen(false);
    }, [setSearchDropdownOpen, setAccountDropdownOpen]);

    const onSearchInputChange = useCallback(
        (e) => {
            const newSearchValue = e.target.value;
            setSearchInput(newSearchValue);
            setAccountDropdownOpen(false);
            setSearchDropdownOpen(true);
        },
        [setSearchInput, setAccountDropdownOpen, setSearchDropdownOpen]
    );

    const handleCloseDropdown = useCallback(() => {
        setSearchDropdownOpen(false);
        if (searchInputRef.current) {
            searchInputRef.current.blur();
        }
    }, [setSearchDropdownOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchContainerRef.current &&
                !searchContainerRef.current.contains(event.target)
            ) {
                setSearchDropdownOpen(false);
            }
        };

        const handleEscKey = (event) => {
            if (event.key === "Escape") {
                setSearchDropdownOpen(false);
                const input = searchContainerRef.current
                    ? searchContainerRef.current.querySelector("input")
                    : null;
                if (input) {
                    input.blur();
                }
            }
        };

        document.addEventListener("keydown", handleEscKey);
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("keydown", handleEscKey);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [setSearchDropdownOpen]);

    useEffect(() => {
        const trimmed = searchInput.trim();

        if (!trimmed || trimmed.length < 3) {
            setSearchTerm("");
            return;
        }

        const handler = setTimeout(() => {
            setSearchTerm(trimmed);
        }, 400);

        return () => {
            clearTimeout(handler);
        };
    }, [searchInput, setSearchTerm]);

    useEffect(() => {
        if (searchDropdownOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [searchDropdownOpen]);

    return (
        <Box sx={{ display: "flex", alignItems: "center", flex: 2 }}>
            <SearchBarContainer ref={searchContainerRef}>
                <StyledInputBase
                    placeholder="Search..."
                    size="small"
                    value={searchInput}
                    inputRef={searchInputRef}
                    onChange={onSearchInputChange}
                    onFocus={handleSearchFocus}
                />

                {!!searchDropdownOpen && (
                    <Paper
                        sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: -200,
                            zIndex: 1000,
                            overflowY: "auto",
                            mt: 1,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: "var(--border-radius-xs)",
                        }}
                    >
                        {(!searchInput.trim() || searchInput.length < 3) && !loading ? (
                            <SearchSuggestionsBlock
                                categoriesAndTypes={categoriesAndTypes}
                                vendors={vendors}
                            />
                        ) : (
                            <ResultsSuggestionsBlock
                                searchInput={searchInput}
                                loading={loading}
                                products={products}
                                handleAddToCart={handleAddToCart}
                            />
                        )}
                    </Paper>
                )}

                {searchDropdownOpen ? (
                    <CloseButton onClick={handleCloseDropdown}>
                        <CloseIcon />
                    </CloseButton>
                ) : (
                    <SearchButton>
                        <SearchIcon sx={{ mr: 0.5 }} />
                        Search
                    </SearchButton>
                )}
            </SearchBarContainer>
        </Box>
    );
};

export default SearchBar;
